import clsx from 'clsx'
import { useState, type FC } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { DialogKirimPredikatOrganisasi } from 'app/KinerjaDinasPemberiPredikatPage/components/DialogKirimPredikatOrganisasi'
import { ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/predicateRecommendation'

import type { TPredicateRecommendationResult } from 'types/kinerja-pelaksanaan'

interface OverviewPredikatOrganisasiProps {
  isLoading?: boolean
  onClickSend?: () => void
  predicateResult: Record<TPredicateRecommendationResult, number>
  separateAfter: number
  ids: Array<string>
}

const OverviewPredikatOrganisasi: FC<OverviewPredikatOrganisasiProps> = ({
  isLoading,
  onClickSend,
  predicateResult,
  separateAfter,
  ids,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false)

  const predicates = (predicateResult && Object.keys(predicateResult)) || []
  const firstPredicates = predicates.slice(0, separateAfter)
  const secondPredicates = predicates.slice(separateAfter)
  const total = ids.length
  const isEmpty = total <= 0

  const handleClickSend = () => {
    onClickSend?.()

    setDialogOpen(true)
  }

  if (isLoading) {
    return (
      <div className="px-6">
        <div className="rounded border border-blue-70 bg-surface-selected-default p-3 flex gap-5">
          <Shimmer width={400} height={120} />
          <Shimmer width={400} height={120} />
          <Shimmer width={400} height={120} />
        </div>
      </div>
    )
  }

  return (
    <div className="px-6">
      <div
        className={clsx(
          'rounded border',
          isEmpty
            ? 'border-disabled bg-gray-5'
            : 'border-blue-70 bg-surface-selected-default'
        )}
      >
        <div className="p-3 flex gap-10">
          <div className="space-y-3">
            <div className="space-y-1.5">
              {isEmpty ? (
                <>
                  <Text variant="heading-sm" color="disabled">
                    Belum ada Predikat Kinerja Organisasi yang dapat dikirim
                  </Text>
                </>
              ) : (
                <>
                  <Text
                    variant="heading-sm"
                    className="!text-icon-informational"
                  >
                    {total} Predikat Kinerja Organisasi dapat dikirim
                  </Text>
                </>
              )}
            </div>
            <Button
              aria-label={`Mulai kirim ${total} predikat kinerja organisasi`}
              className="gap-1.5"
              size="lg"
              disabled={isEmpty}
              onClick={handleClickSend}
            >
              <Icon color={isEmpty ? 'disabled' : 'inverse'}>mail_outline</Icon>
              Kirim ({total})
            </Button>
          </div>
          <div className="flex flex-1 flex-row gap-3">
            <div className="flex flex-1 flex-col gap-3">
              {firstPredicates.map((key) => (
                <div className="flex flex-row justify-between" key={key}>
                  <Text variant="body-bold" color="subdued">
                    {ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT[key]}
                  </Text>
                  <Text>{predicateResult[key]}</Text>
                </div>
              ))}
            </div>
            <hr className="bg-blue-40 w-px h-full" />
            <div className="flex flex-1 flex-col gap-3">
              {secondPredicates.map((key) => (
                <div className="flex flex-row justify-between" key={key}>
                  <Text variant="body-bold" color="subdued">
                    {ORGANIZATION_PREDICATE_RECOMMENDATION_RESULT_TEXT[key]}
                  </Text>
                  <Text>{predicateResult[key]}</Text>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <DialogKirimPredikatOrganisasi
        isOpen={dialogOpen}
        ids={ids}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  )
}
export default OverviewPredikatOrganisasi
